import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import axiosInstance from '../../axiosInstance';

function getPast7Days() {
  const days = [];
  for (let i = 1; i <= 7; i++) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    d.setUTCHours(0, 0, 0, 0);
    days.push(d);
  }
  return days;
}

export default function StreakBackfillDialog({ open, onClose, heatmapData = [], onSuccess }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const days = getPast7Days();
  const activitySet = new Set(heatmapData.filter(d => d.count > 0).map(d => d.date));

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      await axiosInstance.post('/users/me/streak/backfill', { date: selected });
      onSuccess();
      onClose();
      setSelected(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to backfill');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Backfill a Missed Day</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <List dense>
          {days.map(day => {
            const dateStr = day.toISOString().slice(0, 10);
            const hasActivity = activitySet.has(dateStr);
            const label = day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' });
            return (
              <ListItem
                key={dateStr}
                button
                disabled={hasActivity}
                selected={selected === dateStr}
                onClick={() => !hasActivity && setSelected(dateStr)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {hasActivity
                    ? <CheckCircleIcon color="success" fontSize="small" />
                    : <RadioButtonUncheckedIcon color={selected === dateStr ? 'primary' : 'disabled'} fontSize="small" />
                  }
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  secondary={hasActivity ? 'Activity logged' : 'Missed'}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected || loading}
          onClick={handleConfirm}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Backfill
        </Button>
      </DialogActions>
    </Dialog>
  );
}
